import { __awaiter } from "tslib";
import Auth, { CognitoUser } from '@aws-amplify/auth';
import { AuthenticationDetails, CognitoUserPool, CognitoRefreshToken } from 'amazon-cognito-identity-js';
import { sessionService } from '../session';
import { getCognitoEnvironment } from '../kloans-authentication';
class AmplifyService {
    constructor() {
        this.userPoolConfig = null;
        this.cognitoUser = null;
        this.environmentError = new Error('Cognito environment must be preloaded!');
    }
    initialize() {
        return __awaiter(this, void 0, void 0, function* () {
            // Get cognitoEnvironment variables from API and set UserPoolObject
            // this is needed for many of the cognito methods called from this service.
            const environmentResponse = yield getCognitoEnvironment();
            this.setUserPoolObject(environmentResponse.data.data);
        });
    }
    isInitialized() {
        return this.userPoolConfig !== null;
    }
    setUserPoolObject(userPoolConfig) {
        this.userPoolConfig = userPoolConfig;
        this.userPoolConfig.loginKey = `cognito-idp.${userPoolConfig.region}.amazonaws.com/${userPoolConfig.userPoolId}`;
        Auth.configure({
            region: userPoolConfig.region,
            userPoolId: this.userPoolConfig.userPoolId,
            userPoolWebClientId: this.userPoolConfig.clientId
        });
    }
    getUserPool() {
        if (!this.isInitialized()) {
            throw this.environmentError;
        }
        const poolData = {
            UserPoolId: this.userPoolConfig.userPoolId,
            ClientId: this.userPoolConfig.clientId
        };
        return new CognitoUserPool(poolData);
    }
    getCognitoUser() {
        if (!this.isInitialized()) {
            throw this.environmentError;
        }
        if (this.cognitoUser) {
            return this.cognitoUser;
        }
        return this.getUserPool().getCurrentUser();
    }
    signIn({ username, password }, callbacks) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.initialize();
            const authenticationDetails = new AuthenticationDetails({ Username: username, Password: password });
            const userPool = this.getUserPool();
            const userData = { Username: username, Pool: userPool };
            const cognitoUser = new CognitoUser(userData);
            this.cognitoUser = cognitoUser;
            this.cognitoUser.authenticateUser(authenticationDetails, {
                onSuccess: (session) => {
                    this.setSession(session);
                    callbacks.onSuccess();
                },
                mfaRequired: () => {
                    if (callbacks.mfaRequired) {
                        callbacks.mfaRequired(cognitoUser);
                    }
                },
                onFailure: (err) => {
                    // eslint-disable-next-line
                    let message = 'Wrong username and/or password. Please note your Kasasa Login may be different from your Online Banking Login.';
                    if (err.code === 'NotAuthorizedException' && err.message !== 'Incorrect username or password.') {
                        message = 'Password attempts exceeded. Please try again later.';
                    }
                    this.cognitoUser = null;
                    callbacks.onFailure(message);
                }
            });
        });
    }
    refreshJwtToken(callback) {
        const cognitoUser = this.getCognitoUser();
        cognitoUser.getSession((error, session) => {
            const refreshToken = session.getRefreshToken().getToken();
            cognitoUser.refreshSession(new CognitoRefreshToken({
                RefreshToken: refreshToken,
            }), (error, session) => {
                callback(session.getIdToken().getJwtToken());
            });
        });
    }
    submitMFA(cognitoUser, code, rememberDevice, callbacks) {
        if (!this.isInitialized()) {
            throw this.environmentError;
        }
        else if (!cognitoUser) {
            throw new Error('User identity object missing');
        }
        cognitoUser.sendMFACode(code, {
            onSuccess: (session) => {
                this.setSession(session);
                if (rememberDevice) {
                    this.rememberDevice(cognitoUser, callbacks);
                }
                else {
                    callbacks.onSuccess();
                }
            },
            onFailure: () => {
                callbacks.onFailure('Incorrect code, please try again.');
            }
        });
    }
    rememberDevice(cognitoUser, callbacks) {
        if (!this.isInitialized()) {
            throw this.environmentError;
        }
        cognitoUser.setDeviceStatusRemembered({
            onSuccess: () => {
                callbacks.onSuccess();
            },
            onFailure: () => {
                callbacks.onFailure('Unable to remember device.');
            }
        });
    }
    forgetDevice(cognitoUser, callbacks) {
        if (!this.isInitialized()) {
            throw this.environmentError;
        }
        cognitoUser.setDeviceStatusNotRemembered({
            onSuccess: () => {
                callbacks.onSuccess();
            },
            onFailure: () => {
                callbacks.onFailure();
            }
        });
    }
    setSession(session) {
        if (!this.isInitialized()) {
            throw this.environmentError;
        }
        const logins = {};
        const loginKey = this.userPoolConfig.loginKey;
        logins[loginKey] = session.getIdToken().getJwtToken();
        sessionService.authenticate({
            IdToken: session.getIdToken().getJwtToken(),
            AccessToken: session.getAccessToken().getJwtToken(),
        }, this.getUserPool().getClientId());
    }
    changePassword(oldPassword, newPassword, callbacks) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.isInitialized()) {
                yield this.initialize();
            }
            let message = 'Sorry, there was a problem updating your password.';
            const cognitoUser = this.getCognitoUser();
            if (cognitoUser !== null) {
                cognitoUser.getSession((sessionErr) => {
                    if (sessionErr) {
                        callbacks.onFailure(message);
                        return;
                    }
                    cognitoUser.changePassword(oldPassword, newPassword, (err) => {
                        if (err) {
                            switch (err.code) {
                                case 'NotAuthorizedException':
                                    message = 'There was a problem updating your password. Did you enter your current password correctly?';
                                    break;
                                case 'LimitExceededException':
                                    message = 'Password update attempts exceeded. Please try again later.';
                                    break;
                                default:
                                    message = `There was a problem updating your password: ${err.message}`;
                                    break;
                            }
                            callbacks.onFailure(message);
                        }
                        else {
                            callbacks.onSuccess();
                        }
                    });
                });
            }
            else {
                callbacks.onFailure(message);
            }
        });
    }
    signOut() {
        if (this.cognitoUser) {
            this.cognitoUser.signOut();
        }
    }
}
export const amplifyService = new AmplifyService();
